import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Empty,
  Skeleton,
  Segmented,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  ReadOutlined,
} from '@ant-design/icons';

import { errorMessage, startExplanation, waitForJob } from './api';
import {
  Explanation,
  Instrument,
  NewsArticle,
  NewsCuration,
  NewsResult,
  NewsTag,
  Period,
  TagChoice,
  EvidenceMode,
  NewsSource,
  SourceCapabilities,
  ReviewNews,
  NewsOrder,
} from './types';
import NewsRow from './NewsRow';
import { anchorFor, kindColor } from './temporal';
import CorpusGroups from './CorpusGroups';
import { orderNews } from './newsOrdering';
import TagPalette from './TagPalette';
import { canonicalChoice, tagChoiceKey } from './tags';
import {
  percent,
  periodDates,
  readable,
  safeSourceUrl,
  shortDate,
} from './format';

type Props = {
  runId: string;
  period: Period;
  news: NewsResult | undefined;
  loading: boolean;
  error?: string;
  onRetry: () => void;
  instrument: Instrument;
  curationVersion: number;
  onNewsChanged: (item: NewsCuration) => void;
  onEditingChange: (editing: boolean) => void;
  mode?: EvidenceMode;
  source?: NewsSource;
  capabilities?: SourceCapabilities | null;
  onModeChange?: (mode: EvidenceMode) => void;
  onSourceChange?: (source: NewsSource) => void;
  onSearchCorpus?: () => void;
  selectedReviewNews?: ReviewNews[];
  onReviewToggle?: (article: NewsArticle) => void;
};

const EvidencePanel: React.FC<Props> = ({
  runId,
  period,
  news,
  loading,
  error,
  onRetry,
  instrument,
  curationVersion,
  onNewsChanged,
  onEditingChange,
  mode = 'period',
  source = 'pm',
  capabilities,
  onModeChange,
  onSourceChange,
  onSearchCorpus,
  selectedReviewNews = [],
  onReviewToggle,
}) => {
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [newsOrder, setNewsOrder] = useState<NewsOrder>('oldest');
  const [explanation, setExplanation] = useState<Explanation | null>(null);
  const [explaining, setExplaining] = useState(false);
  const [explainError, setExplainError] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [choices, setChoices] = useState<TagChoice[]>([]);
  const [brush, setBrush] = useState<TagChoice | null>(null);
  const request = useRef<AbortController | null>(null);
  const cache = useRef<Record<string, Explanation>>({});
  const periodKey = `${runId}:${period.id}:${source}:${mode}`;
  const key = `${periodKey}:${curationVersion}`;

  useEffect(() => {
    const instrumentLabel = instrument.ric || `Asset ${instrument.id}`;
    setChoices([
      {
        text: instrumentLabel,
        instrument_id: instrument.id,
        ric: instrument.ric,
        canonical_text: instrumentLabel,
      },
      ...[
        'Downtrend',
        'Uptrend',
        'Reversal',
        'Correction',
        'Range trading',
      ].map(text => ({ text })),
    ]);
    setBrush(null);
  }, [instrument.id, instrument.ric, instrument.ticker, instrument.name]);

  useEffect(() => {
    // A curation change invalidates all interpretations, including other periods.
    cache.current = {};
  }, [curationVersion]);

  const addTag = (tag: NewsTag) => {
    const canonical = canonicalChoice(tag);
    setChoices(current =>
      current.some(value => tagChoiceKey(value) === tagChoiceKey(canonical))
        ? current.map(value =>
            tagChoiceKey(value) === tagChoiceKey(canonical) ? canonical : value,
          )
        : [...current, canonical],
    );
    setBrush(current =>
      current && tagChoiceKey(current) === tagChoiceKey(canonical)
        ? canonical
        : current,
    );
  };

  useEffect(() => {
    request.current?.abort();
    setExplanation(cache.current[key] || null);
    setExplaining(false);
    setExplainError('');
    setArticle(null);
    return () => request.current?.abort();
  }, [key]);

  useEffect(() => {
    setArticle(null);
    setShowAll(false);
    setEditingId(null);
  }, [periodKey]);

  const explain = async () => {
    if (explaining) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setExplaining(true);
    setExplainError('');
    try {
      const initial = await startExplanation(
        runId,
        period.id,
        controller.signal,
        {
          window: mode,
          source,
          ...(source === 'corpus' && news?.source_job_id
            ? { evidence_job_id: news.source_job_id }
            : {}),
        },
      );
      const job = await waitForJob(initial, controller.signal);
      if (controller.signal.aborted) return;
      if (
        ['unavailable', 'invalid_response'].includes(job.result.status || '')
      ) {
        throw new Error(
          job.result.summary ||
            'The explanation is unavailable. Please try again.',
        );
      }
      cache.current[key] = job.result;
      setExplanation(job.result);
    } catch (reason) {
      if (!controller.signal.aborted)
        setExplainError(
          errorMessage(reason) ||
            'The explanation could not be generated. Please try again.',
        );
    } finally {
      if (!controller.signal.aborted) setExplaining(false);
    }
  };

  const articles = orderNews(news?.news || [], newsOrder);
  const corpusCoverage =
    news?.corpus?.coverage ||
    (typeof news?.coverage === 'object' ? news.coverage : {});
  const archiveStart =
    typeof corpusCoverage?.archive_start === 'string'
      ? corpusCoverage.archive_start
      : null;
  const archiveEnd =
    typeof corpusCoverage?.archive_end === 'string'
      ? corpusCoverage.archive_end
      : null;
  const windowStart = news?.evidence_window?.start_date || period.start_date;
  const windowEnd = news?.evidence_window?.end_date || period.end_date;
  const outsideArchive =
    source === 'corpus' &&
    Boolean(
      (archiveEnd && Date.parse(windowStart) > Date.parse(archiveEnd)) ||
        (archiveStart && Date.parse(windowEnd) < Date.parse(archiveStart)),
    );
  const citationArticles = orderNews(
    explanation?.evidence?.news || articles,
    'oldest',
  );
  const visibleArticles = showAll ? articles : articles.slice(0, 5);
  const sourceUrl = safeSourceUrl(article?.url);
  const limitations = [
    ...(news?.limitations || []),
    ...(news?.warnings || []),
    ...(period.quality_flags || []).map(readable),
  ];
  const explainUnavailable = editingId
    ? 'Save or cancel your article edit before generating an explanation.'
    : loading
      ? 'Loading the news evidence…'
      : error
        ? 'Retry loading the news below to enable an explanation.'
        : !articles.length
          ? 'An explanation needs news evidence for this period.'
          : '';

  return (
    <section
      className="market-explorer__evidence"
      aria-label="Selected period details"
      aria-live="polite"
    >
      <div className="market-explorer__evidence-heading">
        <div>
          <div className="market-explorer__eyebrow">SELECTED PERIOD</div>
          <h2 style={{ color: kindColor(period.kind) }}>{period.title}</h2>
          <p>{periodDates(period.start_date, period.end_date)}</p>
        </div>
        <Tooltip title="Observed close-to-close change across this period. This measures the move, not how much any individual news event caused.">
          <span
            className={`market-explorer__return ${(period.return_pct || 0) < 0 ? 'is-negative' : 'is-positive'}`}
          >
            {typeof period.return_pct === 'number' &&
              (period.return_pct < 0 ? (
                <ArrowDownOutlined />
              ) : (
                <ArrowUpOutlined />
              ))}{' '}
            {percent(period.return_pct)}
          </span>
        </Tooltip>
      </div>
      <p className="market-explorer__period-summary">
        {period.summary}{' '}
        <Tooltip title="Trading sessions count steps between consecutive usable daily prices stored in PM. They are not simulator sessions. Exchange calendars and holidays are not verified; weekends count when price observations exist.">
          <button
            className="market-explorer__info"
            type="button"
            aria-label="About trading sessions"
          >
            <InfoCircleOutlined />
          </button>
        </Tooltip>
      </p>

      {period.legs?.length ? (
        <div
          className="market-explorer__period-legs"
          aria-label="Reversal legs"
        >
          {period.legs.map(leg => (
            <span key={leg.role}>
              <small>
                {leg.label} · {leg.trading_sessions} trading sessions
              </small>
              <strong
                className={leg.return_pct < 0 ? 'is-negative' : 'is-positive'}
              >
                {percent(leg.return_pct)}
              </strong>
              <small>{periodDates(leg.start_date, leg.end_date)}</small>
            </span>
          ))}
        </div>
      ) : null}
      <div className="market-explorer__evidence-controls">
        <Segmented
          aria-label="News evidence window"
          disabled={Boolean(editingId)}
          value={mode}
          options={[
            { value: 'period', label: 'Whole period' },
            {
              value: 'anchor',
              label:
                period.kind === 'reversal'
                  ? 'Around the turn'
                  : 'Around the anchor',
            },
          ]}
          onChange={value => onModeChange?.(value as EvidenceMode)}
        />
        {mode === 'anchor' && (
          <small>
            {anchorFor(period).label} · {anchorFor(period).date}
          </small>
        )}
        <div className="market-explorer__source-options">
          <Segmented
            aria-label="News evidence source"
            disabled={Boolean(editingId)}
            value={source}
            options={[
              { value: 'pm', label: 'PM news' },
              {
                value: 'corpus',
                label: 'NewsRag',
                disabled: !capabilities?.corpus.available,
              },
            ]}
            onChange={value => onSourceChange?.(value as NewsSource)}
          />
          <Tooltip
            title={
              capabilities?.corpus.message ||
              'Checking local NewsRag availability…'
            }
          >
            <span tabIndex={0} aria-label="About NewsRag availability">
              <InfoCircleOutlined />
            </span>
          </Tooltip>
        </div>
        {news?.evidence_window && (
          <small>
            Evidence dates:{' '}
            {periodDates(
              news.evidence_window.start_date,
              news.evidence_window.end_date,
            )}
          </small>
        )}
        {source === 'corpus' && (
          <div className="market-explorer__corpus-search">
            <p>
              Find verified event groups in the local NewsRag archive. Original
              PM articles remain the sources.
            </p>
            <Button
              onClick={onSearchCorpus}
              loading={loading}
              disabled={Boolean(editingId) || !capabilities?.corpus.available}
            >
              {news ? 'Refresh NewsRag results' : 'Search NewsRag'}
            </Button>
          </div>
        )}
      </div>
      <div className="market-explorer__explain-section">
        {!explanation && (
          <div className="market-explorer__explain-prompt">
            <div>
              <BulbOutlined />
              <span>
                Connect the move to the evidence
                <small>
                  {explainUnavailable ||
                    'Generate a concise interpretation with source citations.'}
                </small>
              </span>
            </div>
            <Tooltip title="AI reviews the measured price movement and the news retrieved for this period. It may identify plausible drivers or conclude that evidence is insufficient. It runs only when you ask.">
              <Button
                type="primary"
                icon={<BulbOutlined />}
                onClick={explain}
                loading={explaining}
                disabled={Boolean(explainUnavailable) || explaining}
              >
                {explaining ? 'Explaining…' : 'Explain this period'}
              </Button>
            </Tooltip>
          </div>
        )}
        {explaining && (
          <p className="market-explorer__muted" role="status">
            Reviewing the selected period and its sources. This may take a few
            minutes…
          </p>
        )}
        {explainError && (
          <Alert
            type="warning"
            showIcon
            title="Explanation unavailable"
            description={explainError}
            action={
              <Button size="small" onClick={explain}>
                Try again
              </Button>
            }
          />
        )}
        {explanation && (
          <div className="market-explorer__explanation">
            <div className="market-explorer__section-heading">
              <h3>
                <BulbOutlined /> Possible drivers
              </h3>
              <Tooltip title="An evidence-based interpretation of historical events. Timing and association do not establish causation.">
                <Tag>
                  AI interpretation <InfoCircleOutlined />
                </Tag>
              </Tooltip>
            </div>
            <p>{explanation.summary}</p>
            {(explanation.drivers || []).map((driver, index) => (
              <div className="market-explorer__driver" key={index}>
                {driver.title && <strong>{driver.title}</strong>}
                <p>{driver.mechanism}</p>
                <div className="market-explorer__citations">
                  {(driver.news_ids || []).map(id => {
                    const cited = citationArticles.find(
                      item => String(item.id) === String(id),
                    );
                    return (
                      <Tooltip
                        key={id}
                        title={
                          cited?.headline ||
                          'This source is not available in the current news list.'
                        }
                      >
                        <Button
                          type="link"
                          size="small"
                          icon={<ReadOutlined />}
                          disabled={!cited}
                          onClick={() => cited && setArticle(cited)}
                        >
                          Source{' '}
                          {citationArticles.findIndex(
                            item => String(item.id) === String(id),
                          ) + 1 || id}
                        </Button>
                      </Tooltip>
                    );
                  })}
                  {driver.evidence_strength && (
                    <span className="market-explorer__muted">
                      {readable(driver.evidence_strength)} evidence
                    </span>
                  )}
                </div>
              </div>
            ))}
            {Boolean(
              explanation.alternatives?.length ||
                explanation.limitations?.length,
            ) && (
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: 'limits',
                    label: 'Alternatives & uncertainty',
                    children: (
                      <ul className="market-explorer__plain-list">
                        {[
                          ...(explanation.alternatives || []),
                          ...(explanation.limitations || []),
                        ].map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    ),
                  },
                ]}
              />
            )}
          </div>
        )}
      </div>

      <div className="market-explorer__section-heading market-explorer__news-heading">
        <h3>
          Related news{' '}
          {articles.length > 0 && (
            <span className="market-explorer__count">{articles.length}</span>
          )}
        </h3>
        <div className="market-explorer__news-tools">
          <Select
            aria-label="Sort related news"
            size="small"
            value={newsOrder}
            disabled={Boolean(editingId) || loading}
            onChange={value => {
              setNewsOrder(value);
              setShowAll(false);
            }}
            options={[
              { value: 'oldest', label: 'Oldest first' },
              {
                value: 'importance',
                label:
                  source === 'corpus' ? 'Most important' : 'Strongest match',
              },
              { value: 'newest', label: 'Newest first' },
            ]}
          />
          {source === 'pm' && (
            <Button
              type="link"
              size="small"
              onClick={onRetry}
              disabled={Boolean(editingId) || loading}
            >
              Refresh
            </Button>
          )}
        </div>
        <Tooltip title="PM tags, instrument aliases and database full-text search retrieve news. Relevance, timing and novelty select the results. Oldest first is the default; change the ordering to prioritize importance or recent publication. Grouped reports are represented by one article; editing or tagging affects that displayed article only.">
          <button
            className="market-explorer__info"
            type="button"
            aria-label="About related news"
          >
            <InfoCircleOutlined />
          </button>
        </Tooltip>
      </div>
      {source === 'corpus' && news && (
        <CorpusGroups news={news} order={newsOrder} />
      )}
      {source === 'corpus' && news && (archiveStart || archiveEnd) && (
        <p className="market-explorer__archive-coverage" role="status">
          Corpus archive:{' '}
          {archiveStart ? shortDate(archiveStart) : 'unknown start'} –{' '}
          {archiveEnd ? shortDate(archiveEnd) : 'unknown end'}
          {outsideArchive
            ? '. The selected evidence window is outside these dates.'
            : ''}
        </p>
      )}
      <TagPalette
        choices={choices}
        active={brush}
        onSelect={setBrush}
        onAdd={addTag}
      />
      {loading && <Skeleton active paragraph={{ rows: 4 }} title={false} />}
      {!loading && error && (
        <Alert
          type="warning"
          showIcon
          title="News could not be loaded"
          description={error}
          action={
            <Button size="small" onClick={onRetry}>
              Try again
            </Button>
          }
        />
      )}
      {!loading && !error && !articles.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            source === 'corpus' && !news
              ? 'Search NewsRag to retrieve verified groups for this evidence window.'
              : outsideArchive
                ? `This period is outside the local corpus archive${archiveEnd && Date.parse(windowStart) > Date.parse(archiveEnd) ? ` (ends ${shortDate(archiveEnd)})` : ''}. Choose PM news or an earlier period.`
                : 'No matching news was found. This period remains interesting, but its cause is unresolved.'
          }
        />
      )}
      {!loading && !error && articles.length > 0 && (
        <ol className="market-explorer__news-list">
          {visibleArticles.map((item, index) => (
            <NewsRow
              key={`${periodKey}:${item.id}`}
              item={item}
              source={source}
              index={index}
              reviewSelected={selectedReviewNews.some(
                link => String(link.news_id) === String(item.id),
              )}
              onReviewToggle={onReviewToggle}
              brush={brush}
              onRead={setArticle}
              onTagResolved={addTag}
              onChanged={onNewsChanged}
              locked={Boolean(editingId && editingId !== String(item.id))}
              onEditing={editing => {
                setEditingId(editing ? String(item.id) : null);
                onEditingChange(editing);
              }}
            />
          ))}
        </ol>
      )}
      {articles.length > 5 && !loading && (
        <Button
          type="link"
          onClick={() => setShowAll(!showAll)}
          disabled={Boolean(editingId)}
        >
          {showAll
            ? 'Show fewer articles'
            : `Show all ${articles.length} articles`}
        </Button>
      )}
      {limitations.length > 0 && (
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: 'coverage',
              label: (
                <span>
                  Coverage notes <InfoCircleOutlined />
                </span>
              ),
              children: (
                <ul className="market-explorer__plain-list">
                  {limitations.map((item, index) => (
                    <li key={index}>{item}</li>
                  ))}
                </ul>
              ),
            },
          ]}
        />
      )}

      <Drawer
        open={Boolean(article)}
        onClose={() => setArticle(null)}
        title="Article evidence"
        size="large"
        className="market-explorer__article-drawer"
      >
        {article && (
          <>
            <div className="market-explorer__eyebrow">
              {article.timestamp
                ? shortDate(article.timestamp)
                : 'DATE UNAVAILABLE'}
              {article.source ? ` · ${article.source}` : ''}
            </div>
            <h2>{article.headline}</h2>
            {article.timing && <Tag>{readable(article.timing)}</Tag>}
            {article.relevance_reason && (
              <p className="market-explorer__article-relevance">
                {article.relevance_reason}
              </p>
            )}
            <h3>
              {article.body ? 'Available article text' : 'Available excerpt'}
            </h3>
            <p className="market-explorer__article-excerpt">
              {article.body ||
                article.excerpt ||
                'No article excerpt is available.'}
            </p>
            {sourceUrl && (
              <Button
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                icon={<LinkOutlined />}
              >
                Open original source
              </Button>
            )}
          </>
        )}
      </Drawer>
    </section>
  );
};

export default EvidencePanel;
